"use client";

import * as jose from "jose";
import { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLocalStorage, useSessionStorage } from "usehooks-ts";
import { formatUnits, Hex } from "viem";
import Client, { ErrCode } from "./client";
import { isAPIError } from "./client";
import {
    AllocationResponse,
    EntityDetails,
    EntitySetupState,
    EntityType,
    GeneratePurchasePermitResponse,
    PrePurchaseCheckResponse,
    PrePurchaseFailureReason,
    PurchasePermit,
    SaleEligibility,
} from "./types";

type EchoContextType = {
    apiURL: string;
    frontendURL: string;
    clientUUID: string;
    saleUUID: string;
    callbackURL: string;
};

const EchoContext = createContext<EchoContextType | null>(null);

interface EchoConfig {
    apiURL: string;
    frontendURL: string;
    clientUUID: string;
    saleUUID: string;
    callbackURL: string;
}

export const EchoProvider = ({ config, children }: { config: EchoConfig; children: React.ReactNode }) => {
    return <EchoContext.Provider value={config}>{children}</EchoContext.Provider>;
};

const authTokenKey = "echo:auth-token";
const oauthStateKey = "echo:oauth";

type OAuthState = {
    state: string;
    codeVerifier: string;
};

interface UseEchoUnauthorizedResponse {
    authenticated: false;
    ready: boolean;
    login: () => Promise<void>;
    completeOAuth: () => Promise<void>;
    logout: () => void;
    client: undefined;
    prePurchaseCheck: undefined;
    generatePurchasePermit: undefined;
    fetchAllocation: undefined;
    readEntity: undefined;
    sonarHomeURL: URL;
}

interface UseEchoAuthorizedResponse {
    authenticated: true;
    ready: boolean;
    login: undefined;
    completeOAuth: undefined;
    logout: () => void;
    client: Client;
    prePurchaseCheck: (req: {
        entityUUID: string;
        entityType: EntityType;
        walletAddress: Hex;
    }) => Promise<PrePurchaseCheckResponse>;
    generatePurchasePermit: (req: {
        entityUUID: string;
        entityType: EntityType;
        walletAddress: Hex;
    }) => Promise<GeneratePurchasePermitResponse<any>>;
    fetchAllocation: (address: Hex) => Promise<AllocationResponse>;
    readEntity: (address: Hex) => Promise<EntityDetails | null>;
    sonarHomeURL: URL;
}

type UseEchoResponse = UseEchoAuthorizedResponse | UseEchoUnauthorizedResponse;

const useEcho = (): UseEchoResponse => {
    const config = useContext(EchoContext);
    if (!config) {
        throw new Error("Echo context not found");
    }

    const [searchParams] = useSearchParams();

    const [authToken, setAuthToken] = useLocalStorage<string | undefined>(authTokenKey, undefined);
    const [oauthState, setOAuthState] = useSessionStorage<OAuthState | undefined>(oauthStateKey, undefined);

    const [ready, setReady] = useState(false);
    const [authenticated, setAuthenticated] = useState<boolean>(false);
    const [client, setClient] = useState<Client | undefined>(undefined);

    const logout = useCallback(() => {
        startTransition(() => {
            setAuthToken(undefined);
            setAuthenticated(false);
            setClient(undefined);
        });
    }, [setAuthToken]);

    // state population on load and when the auth token changes
    useEffect(() => {
        if (!authToken) {
            logout();
            setReady(true);
            return;
        }

        const decoded = jose.decodeJwt(authToken);
        const expiresAt = new Date((decoded.exp as number) * 1000);

        if (expiresAt < new Date()) {
            logout();
            return;
        }

        const timeUntilExpiry = expiresAt.getTime() - Date.now() - 5000; // 5 second buffer
        const logoutTimeout = setTimeout(logout, timeUntilExpiry);

        startTransition(() => {
            setClient(newEchoClient(config.apiURL, authToken));
            setAuthenticated(true);
            setReady(true);
        });

        return () => {
            clearTimeout(logoutTimeout);
        };
    }, [authToken, logout, setClient, setAuthenticated, setReady, config.apiURL]);

    const login = useCallback(async () => {
        const oauthState = crypto.randomUUID();
        const codeVerifier = crypto.randomUUID();

        setOAuthState({ state: oauthState, codeVerifier });

        const codeChallenge = await generateCodeChallenge(codeVerifier);

        const url = new URL("/oauth/authorize", config.frontendURL);
        url.searchParams.set("client_id", config.clientUUID);
        url.searchParams.set("redirect_uri", config.callbackURL);
        url.searchParams.set("response_type", "code");
        url.searchParams.set("state", oauthState);
        url.searchParams.set("code_challenge", codeChallenge);
        url.searchParams.set("saleUUID", config.saleUUID);

        window.location.href = url.href;
    }, [setOAuthState, config]);

    const completeOAuth = useCallback(async () => {
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const error = searchParams.get("error");

        if (oauthState?.state !== state) {
            setOAuthState(undefined);
            throw new Error("State mismatch");
        }

        if (error) {
            throw new Error(`OAuth error: ${error}`);
        }

        if (!code) {
            throw new Error("No authorization code received");
        }

        if (!oauthState?.codeVerifier) {
            throw new Error("No code verifier received");
        }

        // Send the authorization code to the backend
        const resp = await newEchoClient(config.apiURL).oauth.ExchangeAuthorizationCode({
            Code: code,
            CodeVerifier: oauthState.codeVerifier,
            RedirectURI: typeof window !== "undefined" ? window.location.origin + "/oauth/callback" : "",
        });

        startTransition(() => {
            setAuthToken(resp.token);
            setAuthenticated(true);
            setOAuthState(undefined);
        });
    }, [setAuthToken, setAuthenticated, setOAuthState, oauthState, config, searchParams]);

    const generatePurchasePermit = useCallback(
        async ({
            entityUUID,
            entityType,
            walletAddress,
        }: {
            entityUUID: string;
            entityType: EntityType;
            walletAddress: Hex;
        }) => {
            if (!client) {
                throw new Error("Client not authenticated");
            }

            const resp = await client.externalapi.GenerateSalePurchasePermit({
                SaleUUID: config.saleUUID,
                EntityUUID: entityUUID,
                EntityType: entityType,
                PurchasingWalletAddress: walletAddress,
            });

            return {
                Permit: resp.PermitJSON as unknown as PurchasePermit<any>,
                Signature: resp.Signature,
            };
        },
        [client, config]
    );

    const prePurchaseCheck = useCallback(
        async ({
            entityUUID,
            entityType,
            walletAddress,
        }: {
            entityUUID: string;
            entityType: EntityType;
            walletAddress: Hex;
        }) => {
            if (!client) {
                throw new Error("Client not authenticated");
            }

            const resp = await client.externalapi.PrePurchaseCheck({
                SaleUUID: config.saleUUID,
                EntityUUID: entityUUID,
                EntityType: entityType,
                PurchasingWalletAddress: walletAddress,
            });

            return {
                ReadyToPurchase: resp.ReadyToPurchase,
                FailureReason: resp.FailureReason as PrePurchaseFailureReason,
                LivenessCheckURL: resp.LivenessCheckURL,
            };
        },
        [client, config]
    );

    const fetchAllocation = useCallback(
        async (address: Hex): Promise<AllocationResponse> => {
            if (!client) {
                throw new Error("Client not authenticated");
            }

            const resp = await client.externalapi.Allocation({
                SaleUUID: config.saleUUID,
                WalletAddress: address,
            });

            return {
                HasReservedAllocation: resp.HasReservedAllocation,
                ReservedAmountUSD: formatUnits(BigInt(resp.ReservedAmountUSD), 6),
                MaxAmountUSD: formatUnits(BigInt(resp.MaxAmountUSD), 6),
            };
        },
        [client, config]
    );

    const readEntity = useCallback(
        async (address: Hex) => {
            if (!client) {
                throw new Error("Client not authenticated");
            }

            try {
                const resp = await client.externalapi.ReadEntity({
                    SaleUUID: config.saleUUID,
                    WalletAddress: address,
                });

                return {
                    Label: resp.Entity.Label,
                    EntityUUID: resp.Entity.EntityUUID,
                    EntityType: resp.Entity.EntityType as EntityType,
                    EntitySetupState: resp.Entity.EntitySetupState as EntitySetupState,
                    SaleEligibility: resp.Entity.SaleEligibility as SaleEligibility,
                    InvestingRegion: resp.Entity.InvestingRegion,
                    ObfuscatedEntityID: resp.Entity.ObfuscatedEntityID as Hex,
                };
            } catch (error) {
                if (isAPIError(error) && error.code === ErrCode.NotFound) {
                    return null;
                }
                throw error;
            }
        },
        [client, config]
    );

    const sonarHomeURL = useMemo(() => new URL(`/sonar/${config.saleUUID}`, config.frontendURL), [config]);

    if (!authenticated) {
        return {
            ready,
            authenticated,
            login,
            completeOAuth,
            generatePurchasePermit: undefined,
            prePurchaseCheck: undefined,
            fetchAllocation: undefined,
            readEntity: undefined,
            client: undefined,
            logout,
            sonarHomeURL,
        };
    }

    return {
        ready,
        authenticated,
        client: client!,
        prePurchaseCheck,
        generatePurchasePermit,
        fetchAllocation,
        readEntity,
        logout,
        completeOAuth: undefined,
        login: undefined,
        sonarHomeURL,
    };
};

const generateCodeChallenge = async (codeVerifier: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return btoa(String.fromCharCode(...new Uint8Array(hash)))
        .replace(/=/g, "")
        .replace(/\+/g, "-")
        .replace(/\//g, "_");
};

const newEchoClient = (apiURL: string, authToken?: string): Client => {
    const auth = authToken ? { AuthToken: `api:Bearer ${authToken}` } : undefined;
    console.log("newEchoClient", apiURL, auth);
    return new Client(apiURL, {
        auth,
        fetcher: checkJSONFetcher,
    });
};

async function checkJSONFetcher(input: RequestInfo | URL, init?: RequestInit) {
    const resp = await fetch(input, init);

    // if the API returns a 401 at any point, we just log the user out so they can try again
    if (resp.status === 401) {
        localStorage.removeItem(authTokenKey);
    }

    // The generated encore client calls `resp.json()` for successful (2xx) request responses from our backend.
    // This is problematic if the response body is for some reason not a valid JSON, which can happen
    // if some part of the infrastructure in front of the backend intercepts the request and returns a non-JSON response.
    // For example, the WAF might be blocking a request due to rate limits and return a challenge instead.
    //
    // We're proxying the response object here, adding a custom `json` method that catches and rethrows
    // any errors including the original response status and body for debugging.

    return new Proxy(resp, {
        get(target, prop) {
            const original = target[prop as keyof Response];
            if (typeof original === "function") {
                // Bind the method to the target to preserve the correct 'this' context
                return original.bind(target);
            }

            if (prop === "json") {
                // don't do anything if the API returned an actual error and let the encore client handle it.
                if (!target.ok) {
                    return original;
                }

                return async () => {
                    const body = await target.text();
                    try {
                        return JSON.parse(body);
                    } catch (e) {
                        let msg = String(e);
                        msg += `: the original request returned ${target.status}: ${body}`;
                        throw new Error(msg);
                    }
                };
            }

            return original;
        },
    });
}

export default useEcho;
